/* eslint-disable max-len */
import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { Trans } from 'react-i18next';
import { AirDropContext } from '../../../utils/AirDropProvider';
import { updateQueryStringParameter } from '../../../utils/commonUtils.js';
import LangPicker from '../../atoms/LangPicker/LangPicker';
import { ROUTES } from '../../constants/routes.js';

import LogoAcceleratedPathways from '../../../assets/images/logo-accelerated-pathways.png';
import LogoBtecClassrooms from '../../../assets/images/logo-btec-classrooms.png';
import LogoERPI from '../../../assets/images/logo-gris2@2x.png';
import LogoMaryville from '../../../assets/images/logo-maryville.svg';
import LogoMppItaly from '../../../assets/images/logo-mpp-italy.png';
import LogoPearsonDark from '../../../assets/images/PearsonLogo_Horizontal_Blk_RGB.png';
import LogoPearsonLight from '../../../assets/images/PearsonLogo_Horizontal_W_RGB.png';
import LogoPearsonPlus from '../../../assets/images/logo-pearson-plus-light.png';
import LogoRegis from '../../../assets/images/logo-regis.png';
import LogoRevise from '../../../assets/images/logo-revise.png';
import LogoTestHub from '../../../assets/images/test_hub_logo.png';

import SidebarImageLaptopTown from '../../../assets/images/laptop_town.png';
import SidebarImageMathology from '../../../assets/images/mathology-sidebar-image.png';

import './LegacySideBanner.scss';
import useTranslation from '../../hooks/useTranslation';

interface LegacySideBannerProps {
  page?: string;
}

function LegacySideBanner({
  page = '',
}: LegacySideBannerProps) {
  const config: any = useContext(AirDropContext);
  const { theme, logoTreatment, role, languagePicker } = config;
  const IDP_DISPLAY_NAME = config?.IDP_DISPLAY_NAME ? config?.IDP_DISPLAY_NAME : '';
  const { translateText } = useTranslation();
  const monlabRegUrl = 'https://www.erpi.com/fr/aide/collegial-universitaire/mon-lab-xl.html';

  // urls for clinical_canada
  const ccRegUrl = 'https://www.pearsonclinical.ca/en/ordering/qualification-form.html';
  const ccRegUrlFRCA = 'https://www.pearsonclinical.ca/fr/ordering/qualifications-form.html';

  // urls for etext_2 and pearsonPlus
  const pearsonPlusUrl = 'https://www.pearson.com/store/en-us/pearsonplus/login';
  const eReaderUrl = 'https://www.pearson.com/en-us/pearsonplus.html';

  let logoUrl = theme === 'light-on-dark' ? LogoPearsonLight : LogoPearsonDark;
  let logoClass = 'logo-img';
  let logoBlurbHeading = '';
  let logoBlurb: string | React.ReactNode = translateText('Hello. Sign in and let the learning begin!');
  let sidebarImageUrl = '';
  let sidebarImageClass = 'sidebar-image';
  let sidebarImagePlacement = 'normal';

  const fullPath = (path: string) => {
    const { search } = useLocation();
    const val = search.toString();
    const url = search ? `${path}${val}` : path;
    return url;
  };
  const createAccountUrlFRCA = updateQueryStringParameter(fullPath(ROUTES.CREATE_ACCOUNT_INTERNAL), 'lang', 'fr_CA');
  const createAccountUrl = updateQueryStringParameter(fullPath(ROUTES.CREATE_ACCOUNT_INTERNAL), 'lang', 'en_US');

  switch (logoTreatment) {
    case 'accelerated_pathways':
      logoUrl = LogoAcceleratedPathways;
      logoClass = 'logo-img accelerated-pathways';
      break;

    case 'btec_classrooms':
      logoUrl = LogoBtecClassrooms;
      break;

    case 'dpo':
      logoBlurb = translateText('Signing in is the most secure way to verify your identity prior to submitting your data request. Sign in and begin your request here!');
      break;

    case 'erpi':
      logoUrl = LogoERPI;
      logoClass = 'logo-img erpi';
      logoBlurbHeading = 'MaBiblio';
      logoBlurb = translateText('Sign in to access your digital products or to subscribe to new ones.');
      sidebarImageUrl = SidebarImageLaptopTown;
      break;

    case 'etext_2':
      logoBlurb = (
        <>
          <span>
            {translateText('Hello. Sign in and let the learning begin!')}
          </span>
          <br />
          <br />
          <p className="logo-blurb">
            {translateText('Looking to sign into Pearson+?')}
            <br />
            <a href={pearsonPlusUrl} rel="noreferrer" target="_blank" title="opens in a new tab or window">{translateText('Sign in here')}</a>
            .
          </p>
        </>
      );
      break;

    case 'longman_hk':
      logoBlurb = translateText('Hello. Sign in with your MyPearson ID as your Username, and let the learning begin!');
      break;

    case 'maryville':
      logoUrl = LogoMaryville;
      logoBlurb = translateText('Please sign in to complete your enrollment');
      break;

    case 'mathology':
      logoClass = 'logo-img mathology';
      sidebarImageUrl = SidebarImageMathology;
      sidebarImageClass = 'sidebar-image mathology';
      sidebarImagePlacement = 'above_logo_blurb';
      break;

    case 'monlab_xl':
      logoUrl = LogoERPI;
      logoClass = 'logo-img erpi';
      logoBlurb = (
        <>
          <p className="logo-blurb-text">
            {translateText('Important update!')}
          </p>
          <p className="logo-blurb-text">
            <Trans i18nKey="logoBlurbMonlab2">
              As of August 25, 2024, your product is now accessible via the
              {' '}
              <cite>i+ Interactive platform</cite>
              .
            </Trans>
          </p>
          <p className="logo-blurb-text">
            <Trans i18nKey="logoBlurbMonlab">
              If you have a MonLab xL code,
              {' '}
              <a href={monlabRegUrl} rel="noreferrer" target="_blank" title="opens in a new tab or window">click here</a>
              .
            </Trans>
          </p>
        </>
      );
      break;

    case 'mpp_italy':
      logoUrl = LogoMppItaly;
      logoClass = 'logo-img mpp-italy';
      logoBlurb = '';
      break;

    case 'pearson_plus':
      logoUrl = LogoPearsonPlus;
      logoBlurb = (
        <p className="logo-blurb-text">
          <Trans i18nKey="logoBlurbPearsonPlus">
            Your eTextbooks, videos, and study tools
            {' '}
            <a href={eReaderUrl} rel="noreferrer" target="_blank" title="opens in a new tab or window">in one place</a>
            .
          </Trans>
        </p>
      );
      break;

    case 'regis':
      logoUrl = LogoRegis;
      logoBlurb = translateText('Please sign in to complete your enrollment');
      break;

    case 'revise_plus_exam_prep':
      logoUrl = LogoRevise;
      logoBlurb = translateText('Unbeatable revision and exam preparation - all in one place.');
      break;

    case 'test_hub':
      logoUrl = LogoTestHub;
      logoClass = 'logo-img test-hub';
      logoBlurb = translateText('Bringing teachers, tests and test-takers together');
      break;

    case 'clinical_canada':
      // note: no translations needed since the French is hard-coded below (by design)
      logoBlurb = (
        <>
          <p className="logo-blurb-text head">
            Welcome to Pearson Clinical Assessment
          </p>
          <p className="logo-blurb-text small">
            <strong>New Customers:</strong>
            <br />
            Please set up an
            {' '}
            <a href={createAccountUrl}>
              online account
            </a>
            {' '}
            to take advantage of our site features and quick checkout process.
          </p>
          <p className="logo-blurb-text small">
            To avoid any order processing delays, please ensure to register yourself as a qualified user. Visit our
            {' '}
            <a href={ccRegUrl} rel="noreferrer" target="_blank" title="opens in a new tab or window">Customer Registration page</a>
            {' '}
            to complete the online Customer Registration form.
          </p>
          <p className="logo-blurb-text small">
            <strong>Nouveaux clients :</strong>
            <br />
            Créer un
            {' '}
            <a href={createAccountUrlFRCA}>
              compte en ligne
            </a>
            {' '}
            et profiter de nombreux avantages et passer vos commandes en toute sécurité.
          </p>
          <p className="logo-blurb-text small">
            Afin d’éviter tout délai dans le processus de votre commande, nous vous invitons à vous inscrire en tant qu’utilisateur qualifié. Pour cela, veuillez visiter la
            {' '}
            <a href={ccRegUrlFRCA} rel="noreferrer" target="_blank" title="le lien va s'ouvrir dans un nouvel onglet ou une nouvelle fenêtre">page d’inscription</a>
            {' '}
            afin de remplir le formulaire en ligne.
          </p>
        </>
      );
      break;

    default:
      break;
  }

  // special case, override logoBlurb for these pages
  if (page === 'forgot-unpw' || page === 'reset-pw') {
    logoBlurb = translateText('Forgot your username or password? Let us help.');
  }

  const isConsentsPage = page === 'consents';
  // special case, override logoBlurb and logoClass for consents pages
  if (page === 'consents') {
    logoBlurb = null;
  }

  if (page === 'auo-welcome' && role === 'INSTRUCTOR') {
    logoBlurb = (
      <Trans i18nKey="auoWelcomeInstructorLogoBlurb" values={{ IDP_DISPLAY_NAME }}>
        {`It’s your first time accessing Pearson through this ${IDP_DISPLAY_NAME} account.`}
      </Trans>
    );
  }

  return (
    <div className={`side-banner-legacy ${isConsentsPage ? 'consents' : ''}`}>
      <div className="logo-container">
        <div className="logo">
          <img src={logoUrl} className={logoClass} alt="Logo" />
        </div>
        {sidebarImageUrl && sidebarImagePlacement === 'above_logo_blurb' && (
          <img src={sidebarImageUrl} className={sidebarImageClass} alt="" />
        )}
        {logoBlurbHeading && (
          <div className="logo-blurb-heading">
            {logoBlurbHeading}
          </div>
        )}
        {logoBlurb && (
          <div className="logo-blurb">
            {logoBlurb}
          </div>
        )}
        {sidebarImageUrl && sidebarImagePlacement === 'normal' && (
          <img src={sidebarImageUrl} className={sidebarImageClass} alt="" />
        )}
      </div>
      {languagePicker.show && (
        <LangPicker />
      )}
    </div>
  );
}

export default LegacySideBanner;
